import styled, { keyframes } from 'styled-components';
import React, { useEffect, useState,useRef } from "react";
import Chart from "react-apexcharts";

import ItemTable from './ItemTable';
import PptxGenJS from 'pptxgenjs';
import StarImg from 'assets/images/UI/star.svg';  // Assuming this is the SVG data or path
import ArrowImg from 'assets/images/UI/arrow_blue.svg';  // Assuming this is the SVG data or path
import { injectDownloadOptions } from "constants/chartOptions";


const SummaryChart = ({selectedFactor,chartData,setSelected}) =>{
  const chartRef = useRef(null); 
  const [loading, setLoading] = useState(true)
  const chartContainerRef = useRef(null);
  const svgOverlayRef = useRef(null);
  const [dotPositions, setDotPositions] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 800, height: 400 });
    const [selectedQuestion, setSelectedQuestion] = useState('Overall Summary');
  // Combine "Overall Summary" with the list of questions
  const questionOptions = ['Overall Summary', ...selectedFactor.questions.map((q, index) => q.q)];



    const [selectedOption, setSelectedOption] = useState('Average Score');

    useEffect(()=>{
        setTimeout(()=>{
            setLoading(false)
        },500)
    },[])

    // Process the chart data to apply sorting and scaling
  const processedChartData = React.useMemo(() => {
    if (!chartData) {
      return {
        categories: [],
        series: [],
        xAxisMax: null,
        labelsFormatter: (val) => val,
        tooltipFormatter: (val) => val,
      };
    }

    const isOverallSummary = selectedQuestion === 'Overall Summary';

    let seriesData;
    if (isOverallSummary) {
      seriesData = chartData.series.find((s) => s.name === selectedOption);
    } else {
        seriesData = chartData.questionSeries[selectedQuestion]?.[selectedOption];
    }

    if (!seriesData) {
      return {
        categories: [],
        series: [],
        xAxisMax: null,
        labelsFormatter: (val) => val,
        tooltipFormatter: (val) => val,
      };
    }

    let scaledData;
    let xAxisMax;
    let labelsFormatter;
    let tooltipFormatter;

    if (selectedOption === 'Average Score') {
      scaledData = seriesData.data.map((v) => (v !== null ? Math.round(v * 10) : null));
        xAxisMax = 100;
      labelsFormatter = (val) => val.toString();
      tooltipFormatter = (val) => val.toString();
    } else if (selectedOption === 'Max Score' || selectedOption === 'Min Score') {
      scaledData = seriesData.data.map((v) => (v !== null ? Math.round(v) : null));
        xAxisMax = 10;
      labelsFormatter = (val) => val.toString();
      tooltipFormatter = (val) => val.toString();
    } else if (selectedOption === 'Standard Deviation') {
      scaledData = seriesData.data.map((v) => (v !== null ? parseFloat(v.toFixed(2)) : null));
      xAxisMax = null;
      labelsFormatter = (val) => val.toFixed(2);
      tooltipFormatter = (val) => val.toFixed(2);
    } else {
        scaledData = seriesData.data;
        xAxisMax = null;
      labelsFormatter = (val) => val.toString();
      tooltipFormatter = (val) => val.toString();
    }

    // Get change data
    const changeSeries = isOverallSummary
      ? chartData.series.find((s) => s.name === 'Change')
      : chartData.questionSeries[selectedQuestion]?.Change;

    // Calculate previous scores from current scores and changes, rounding to integers
    const previousScores = scaledData.map((currentScore, index) => {
      const lastScore = chartData?.lastSeries[0]?.data[index]
      if (
        currentScore !== null &&
        !isNaN(currentScore) &&
        lastScore !== null &&
        !isNaN(lastScore)
      ) {
        return Math.round(lastScore*10);
      }
      return undefined; // ✅ change this from null to undefined
    });


    // Combine score + previous score and sort by score descending
    const combined = chartData.categories
      .map((category, index) => ({
        category,
        score: scaledData[index],
        previousScore: previousScores[index],
        change: changeSeries?.data?.[index],
      }))
      .filter((d) => d.score !== null)
      .sort((a, b) => b.score - a.score);


    const sortedCategories = combined.map((d) => d.category);
    const sortedValues = combined.map((d) => d.score);
    const sortedPreviousScores = combined.map((d) => d.previousScore);

    const sortedChanges = combined.map((d) => d.change);

    // Check if any previous scores exist
    const hasPreviousScores = sortedPreviousScores.some(score => typeof score === 'number' && !isNaN(score));

    const processedSeries = [
        {
        name: seriesData.name,
        type: 'bar',
        data: sortedValues,
      },
    ];

    // Only add Previous Score series if there are previous scores
    if (hasPreviousScores) {
      processedSeries.push({
        name: 'Previous Score',
        type: 'line',
        data: sortedPreviousScores,
      });
    }


    return {
        categories: sortedCategories,
        series: processedSeries,
        xAxisMax,
        labelsFormatter,
      tooltipFormatter,
      changes: sortedChanges,
      hasPreviousScores,
    };
  }, [chartData, selectedOption, selectedQuestion]);

    // Update x-axis title to reflect the selected option and question
    const xAxisTitle = selectedOption;

    // Update chart title
    const chartTitle = selectedQuestion === 'Overall Summary' ? 'Overall Summary' : selectedQuestion;

  const allPreviousScoresPresent = processedChartData.series[1]?.data.every(val => typeof val === 'number' && !isNaN(val));
  const chartType = allPreviousScoresPresent ? "bar" : "bar";

    const options = {
      chart: {
        type: chartType,
        stacked: false,
        id: "my-chart",
        fontFamily: "Raleway, sans-serif",
        foreColor: "#7E7E7E",
        zoom: { enabled: false },
        toolbar: { show: false },
        events: {
          mounted: (chartContext) => {
            // Delay to ensure DOM elements are rendered
            setTimeout(() => {
              const chartElement = chartContext.el;
              const dots = chartElement.querySelectorAll(
                'g.apexcharts-series[seriesname="Previous Score"] circle'
              );

              const positions = Array.from(dots).map((dot) => {
                const cx = parseFloat(dot.getAttribute('cx'));
                const cy = parseFloat(dot.getAttribute('cy'));
                return { x: cx, y: cy };
              });

              setDotPositions(positions);
            }, 100); // Adjust delay as needed
          },
        },
      },
      colors: processedChartData.hasPreviousScores ? ["#2D70E2", "#FEA711"] : ["#2D70E2"],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 5,
          barHeight: "70%",
        },
      },
      legend: {
        show: processedChartData.hasPreviousScores,
        position: 'top',
        horizontalAlign: 'right',
        fontFamily: 'Raleway, sans-serif',
        markers: {
          width: 12,
          height: 12
        },
        itemMargin: {
          horizontal: 10
        }
      },
      fill: { type: "solid" },
      grid: {
        xaxis: { lines: { show: processedChartData.hasPreviousScores } },
        yaxis: { lines: { show: false } },
      },
      xaxis: {
        categories: processedChartData.categories,
        title: { text: xAxisTitle },
        labels: {
          formatter: processedChartData.labelsFormatter,
          style: { fontFamily: "Raleway, sans-serif" },
        },
        max: processedChartData.xAxisMax,
      },
      yaxis: {
        labels: {
          style: { fontFamily: "Raleway, sans-serif" },
        },
        min: 0,
        max: 100,
      },
      dataLabels: {
        enabled: true,
        formatter: function (val, { seriesIndex }) {
          if (seriesIndex === 1) {
            return "";
          }

          return `${val}%`;
        },
        style: {
          fontSize: "14px",
          fontFamily: "Raleway, sans-serif",
          colors: ["#FFFFFF", "#000000"],
        },
        offsetY: 0,
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000",
          opacity: 0.3,
        },
      },
      tooltip: {
        enabled: true,
        // Custom label in tooltip, needs to have % sign
        custom: function ({ series, seriesIndex, dataPointIndex, w, options }) {
          const currentScore = series[0][dataPointIndex];
          const previousScore = series[1] ? series[1][dataPointIndex] : null;
          const category = w.globals.labels[dataPointIndex];

          return `
            <div style="padding: 8px; background-color: white; border: 1px solid #e0e0e0; border-radius: 5px; font-family: Raleway, sans-serif;">
              <div style="font-size: 14px; font-weight: 500; color: #333; padding-bottom: 8px; border-bottom: 1px solid #e0e0e0;">
                ${category}
              </div>
              <div style="margin-top: 8px;">
                <div style="display: flex; align-items: center; margin-bottom: 4px;">
                  <span style="display: inline-block; width: 12px; height: 2px; background: #2E93fA; margin-right: 8px;"></span>
                  <span>Current Score: ${currentScore}%</span>
                </div>
                ${previousScore && typeof previousScore === 'number' && !isNaN(previousScore) ? `<div style="display: flex; align-items: center;">
                  <span style="display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: #FEA711; margin-right: 8px;"></span>
                  <span>Previous Score: ${previousScore}%</span>
                </div>` : ''}
              </div>
            </div>
          `;
        }
      },
      title: {
        text: chartTitle,
        align: "center",
        style: {
          fontFamily: "Raleway, sans-serif",
        },
      },
      markers: {
        size: processedChartData.hasPreviousScores ? [0, 6] : [0],
        colors: processedChartData.hasPreviousScores ? ['#2D70E2', '#FEA711'] : ['#2D70E2'], // Blue for Average Score, Orange for Previous Score
        strokeWidth: 0,
        hover: { size: 8 }
      },
      stroke: {
        show: processedChartData.hasPreviousScores,
        width: processedChartData.hasPreviousScores ? [0, 3] : [0],
        curve: 'smooth',
        colors: ['#FEA711'],
      }
    };

    // Handle radio button change
    const handleOptionChange = (event) => {
      setSelectedOption(event.target.value);
    };

    // Handle question dropdown change
    const handleQuestionChange = (event) => {
      setSelectedQuestion(event.target.value);
    };

  // useEffect(() => {
  //   if (!chartContainerRef.current) return;

  //   const observer = new MutationObserver(() => {
  //     const svg = chartContainerRef.current.querySelector('svg');
  //     const circles = svg?.querySelectorAll(
  //       'g.apexcharts-series[seriesName="Previous Score"] circle'
  //     );

  //     if (!circles || circles.length === 0) return;

  //     const positions = Array.from(circles)
  //       .map((circle) => {
  //         const cx = circle.getAttribute('cx');
  //         const cy = circle.getAttribute('cy');
  //         if (!cx || !cy) return null;
  //         return {
  //           x: parseFloat(cx),
  //           y: parseFloat(cy),
  //         };
  //       })
  //       .filter(Boolean);

  //     if (positions.length > 1) {
  //       setDotPositions(positions);
  //       observer.disconnect(); // stop watching once we get them
  //     }
  //   });

  //   observer.observe(chartContainerRef.current, {
  //     childList: true,
  //     subtree: true,
  //   });

  //   return () => observer.disconnect();
  // }, [processedChartData]);


    return (
      <Container>
        {/* Radio Buttons */}
        {/* <div style={{ marginBottom: '20px' }}>
                <label style={{marginRight:10}}>
                    <input
                        type="radio"
                        value="Average Score"
                        checked={selectedOption === 'Average Score'}
                        onChange={handleOptionChange}
                    />
                    Average Score
                </label>
                {' '}
                <label style={{marginRight:10}}>
                    <input
                        type="radio"
                        value="Max Score"
                        checked={selectedOption === 'Max Score'}
                        onChange={handleOptionChange}
                    />
                    Max Score
                </label>
                {' '}
                <label style={{marginRight:10}}>
                    <input
                        type="radio"
                        value="Min Score"
                        checked={selectedOption === 'Min Score'}
                        onChange={handleOptionChange}
                    />
                    Min Score
                </label>
                {' '}
                <label style={{marginRight:10}}>
                    <input
                        type="radio"
                        value="Standard Deviation"
                        checked={selectedOption === 'Standard Deviation'}
                        onChange={handleOptionChange}
                    />
                    Standard Deviation
                </label>
            </div> */}

        {/* <div style={{ marginBottom: '20px' }}>
                <select value={selectedQuestion} onChange={handleQuestionChange} style={{ width: '100%', padding: '8px', fontFamily: 'Raleway, sans-serif' }}>
                    {questionOptions.map((option, index) => (
                        <option key={index} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            </div> */}
        <div
          ref={chartContainerRef}
          style={{
            position: "relative",
            height: 400,
            opacity: loading ? 0 : 1,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          {processedChartData?.series[0]?.data?.length > 0 && (
            <Chart
              options={injectDownloadOptions(options, true, true)}
              series={processedChartData.series}
              type={chartType}
              height={400}
            />
          )}
        </div>
        {/* <button onClick={exportToPowerPoint}>Export to PowerPoint</button> */}

        <ItemTable chartData={chartData} setSelected={setSelected} />
      </Container>
    );
}

export default SummaryChart;


const Container = styled.div`
    margin-top: 20px;
`



